"use client";

import { useEffect, useState } from "react";
import { LuSearch, LuTrash2, LuUserPlus, LuUserX, LuX } from "react-icons/lu";

import { usePermissionsContext } from "~/app/providers";

import Modal, { ModalFooter } from "~/components/v3/themed/Modal";
import { Button } from "~/components/v4/themed/Button";
import { toastHandler } from "~/components/ToastHandler";
import { ConfirmActionModal } from "~/components/v4/shadcn/ConfirmActionModal";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { useDebounce } from "~/hooks/useDebounce";

import {
    addHost,
    addHostPreapproval,
    removeExternalLink,
    removeHost,
} from "./actions";
import { RiLinkM, RiMailSendLine } from "react-icons/ri";
import { useQuery } from "@tanstack/react-query";
import { searchUser } from "~/app/_actions/user";
import { If } from "~/components/v3/Conditional";
import { ButtonLink } from "~/components/v3/themed/Button";
import { CustomizableLayout } from "~/types/CustomizableLayout";
import { cn } from "~/utils/fontsConfig";

export function DeleteHostButton({
    eventId,
    eventSlug,
    hostId,
    hostUserId,
    isCreator,
    isLastHost,
    customization,
}: {
    eventId: string;
    eventSlug: string;
    hostId: string;
    hostUserId: string;
    isCreator: boolean;
    isLastHost: boolean;
    customization?: CustomizableLayout;
}) {
    const [isPending, setIsPending] = useState(false);
    const permissions = usePermissionsContext();
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const handleRemoveHost = () => {
        setIsPending(true);

        toastHandler({
            loadingMessage: "Removing host...",
            successMessage: "Host removed successfully",
            operation: () => removeHost({ eventId, hostId, hostUserId }),
            onSettled: () => {
                setIsPending(false);
            },
        });
    };

    return (
        <div>
            <button
                onClick={
                    isCreator
                        ? () => setShowConfirmModal(true)
                        : handleRemoveHost
                }
                className="text-v3-N-300 hover:text-white"
                disabled={isPending}
            >
                <LuTrash2 className="h-5 w-5" />
            </button>

            <If
                condition={
                    isLastHost &&
                    (!permissions.isAdmin || !permissions.isManager)
                }
            >
                <Modal
                    open={showConfirmModal}
                    onOpenChange={setShowConfirmModal}
                    title="Cancel Event"
                    description={cn(
                        "You cannot delete the only host of this event, but you can cancel the event instead.",
                        customization?.descriptionClassName,
                    )}
                    className={customization?.mainCardClassName}
                    icon={<LuUserX className="h-6 w-6" />}
                    footer={
                        <ButtonLink
                            variant="secondary"
                            className={cn(
                                "text-v3-N-300 hover:text-white",
                                customization?.buttons?.secondary,
                            )}
                            href={`/beta/events/${eventSlug}/manage/more`}
                        >
                            Go to Settings
                        </ButtonLink>
                    }
                ></Modal>
            </If>
            <If
                condition={
                    !isLastHost ||
                    !!permissions.isAdmin ||
                    !!permissions.isManager
                }
            >
                <ConfirmActionModal
                    isOpen={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    Icon={<LuUserX className="h-6 w-6" />}
                    title="Remove Creator From Event Hosts"
                    description="Are you sure you want to remove the event creator from the event hosts?"
                    customization={customization}
                    onConfirm={handleRemoveHost}
                    confirmText="Remove Host"
                    isLoading={isPending}
                    variant="destructive"
                />
            </If>
        </div>
    );
}

export const DeleteExternalLinkButton = ({
    eventId,
    customization,
}: {
    eventId: string;
    customization?: CustomizableLayout;
}) => {
    const [isPending, setIsPending] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const handleRemoveLink = () => {
        setIsPending(true);

        toastHandler({
            loadingMessage: "Removing host...",
            successMessage: "Host removed successfully",
            operation: () => removeExternalLink({ eventId }),
            onSettled: () => {
                setIsPending(false);
            },
        });
    };

    return (
        <div>
            <button
                onClick={() => setShowConfirmModal(true)}
                className="text-v3-N-300 hover:text-white"
                disabled={isPending}
            >
                <LuTrash2 className="h-5 w-5" />
            </button>

            <ConfirmActionModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                Icon={<RiLinkM className="h-6 w-6" />}
                title="Remove External Event Link"
                description="Are you sure you want to remove external link and convert it to a regular River event?"
                onConfirm={handleRemoveLink}
                confirmText="Remove Link"
                isLoading={isPending}
                variant="destructive"
                customization={customization}
            />
        </div>
    );
};

export const AddHostButton = ({
    eventId,
    communityId,
    customization,
}: {
    eventId: string;
    communityId?: string | null;
    customization?: CustomizableLayout;
}) => {
    const [open, setOpen] = useState(false);
    const permissions = usePermissionsContext();

    const isAllowed =
        permissions.isHost || permissions.isManager || permissions.isAdmin;

    return (
        <div>
            <Button
                variant="default"
                size="xs"
                className={cn(
                    "rounded-lg bg-white/15 p-3 text-xs text-white",
                    customization?.buttons?.secondary,
                )}
                onClick={() => setOpen(true)}
                disabled={!isAllowed}
            >
                Add Host
            </Button>

            <AddHostModal
                open={open}
                onOpenChange={setOpen}
                eventId={eventId}
                communityId={communityId}
                customization={customization}
            />
        </div>
    );
};

export const AddHostModal = ({
    open,
    onOpenChange,
    eventId,
    communityId,
    customization,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    eventId: string;
    communityId?: string | null;
    customization?: CustomizableLayout;
}) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

    // Debounce the search query to avoid excessive API calls
    const debouncedSearchQuery = useDebounce(searchQuery, 200);

    const { data: foundUsers, isFetching: isSearching } = useQuery({
        queryKey: ["searchUser", debouncedSearchQuery, eventId, communityId],
        queryFn: () =>
            searchUser({
                searchQuery: debouncedSearchQuery,
                eventId,
                communityId: communityId ?? undefined,
            }),
        enabled: debouncedSearchQuery.length > 2, // Only start searching after at least 3 characters
        staleTime: 30000, // Use cached results for 30 seconds
        refetchOnWindowFocus: false, // Prevent refetching on window focus
    });

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setSelectedUsers([]);
            setSearchQuery("");
        }
    }, [open]);

    const handleAddHosts = async () => {
        if (selectedUsers.length === 0) return;

        setIsLoading(true);
        await toastHandler({
            loadingMessage: "Adding host(s)...",
            successMessage: "Host(s) added successfully",
            errorMessage: "Failed to add host(s). Please try again.",
            operation: () =>
                addHost({
                    eventId,
                    userIds: selectedUsers.map((user) => user.id),
                }),
            onSettled: () => {
                setIsLoading(false);
                onOpenChange(false);
                setSelectedUsers([]);
                setSearchQuery("");
            },
        });
    };

    const handleUserSelect = (user: any) => {
        // Check if user is already selected
        if (!selectedUsers.some((selected) => selected.id === user.id)) {
            setSelectedUsers([...selectedUsers, user]);
            setSearchQuery(""); // Clear search when user is selected
        }
    };

    const sendHostInviteLink = async (email: string) => {
        setIsLoading(true);
        await toastHandler({
            loadingMessage: "Sending invite for hosting...",
            successMessage: "Sent successfully",
            errorMessage: "Failed to send invite.",
            operation: () => addHostPreapproval({ eventId, email }),
            onSettled: () => {
                setIsLoading(false);
                onOpenChange(false);
                setSelectedUsers([]);
                setSearchQuery("");
            },
        });
    };

    const removeSelectedUser = (userId: string) => {
        setSelectedUsers(selectedUsers.filter((user) => user.id !== userId));
    };

    return (
        <Modal
            open={open}
            onOpenChange={onOpenChange}
            title="Add Hosts"
            description="Add multiple hosts/co-hosts to the event"
            icon={<LuUserPlus className="h-6 w-6" />}
            customization={customization}
        >
            <div className="space-y-4 py-4">
                <div className="space-y-4">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search for users by name or email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className={cn(
                                "h-10 w-full rounded-md border border-v3-N-500 bg-v3-N-500 px-3 py-2 pl-10 text-white placeholder-v3-N-300 focus:outline-none focus:ring-2 focus:ring-v3-primary",
                                customization?.inputClassName,
                            )}
                        />
                        <LuSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-v3-N-300" />
                    </div>

                    {isSearching && (
                        <div
                            className={cn(
                                "py-2 text-center text-sm text-v3-N-300",
                                customization?.descriptionClassName,
                            )}
                        >
                            Searching...
                        </div>
                    )}

                    {searchQuery.length > 0 && !isSearching && (
                        <div
                            className={cn(
                                "max-h-[200px] overflow-y-auto rounded-md bg-v3-N-600",
                                customization?.mainCardClassName,
                            )}
                        >
                            {foundUsers && foundUsers.length > 0 ? (
                                <div className="p-1">
                                    {foundUsers.map((user) => (
                                        <div
                                            key={user.id}
                                            className={cn(
                                                "flex cursor-pointer items-center rounded-md p-2 hover:bg-v3-N-500",
                                                customization?.mainCardClassName,
                                                customization?.defaultTheme ===
                                                    "dark"
                                                    ? "hover:bg-v3-N-500"
                                                    : "hover:bg-black/10",
                                            )}
                                            onClick={() =>
                                                handleUserSelect(user)
                                            }
                                        >
                                            <UserAvatar user={user} size={16} />
                                            <div className="flex flex-col">
                                                <span
                                                    className={cn(
                                                        "ml-3 text-white",
                                                        customization?.descriptionClassName,
                                                    )}
                                                >
                                                    {user.name ?? user.email}
                                                </span>
                                                <span
                                                    className={cn(
                                                        "ml-3 line-clamp-1 text-sm text-v3-N-300",
                                                        customization?.descriptionClassName,
                                                    )}
                                                >
                                                    {user.bio}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center">
                                    <div className="p-3 text-center text-sm text-v3-N-300">
                                        No users found
                                    </div>
                                    {/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(
                                        searchQuery,
                                    ) ? (
                                        <Button
                                            size="sm"
                                            variant="tertiary"
                                            className="ml-2"
                                            onClick={() => {
                                                sendHostInviteLink(searchQuery);
                                            }}
                                        >
                                            <RiMailSendLine />
                                            send host invite link
                                        </Button>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {selectedUsers.length > 0 && (
                    <div className="mt-4">
                        <div className="mb-2 text-sm text-v3-N-300">
                            Selected users ({selectedUsers.length}):
                        </div>
                        <div className="space-y-2">
                            {selectedUsers.map((user) => (
                                <div
                                    key={user.id}
                                    className={cn(
                                        "rounded-md bg-v3-N-500 p-3",
                                        customization?.mainCardClassName,
                                    )}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <UserAvatar
                                                user={user}
                                                className="mr-3 h-8 w-8"
                                            />
                                            <div>
                                                <p
                                                    className={cn(
                                                        "font-medium text-white",
                                                        customization?.descriptionClassName,
                                                    )}
                                                >
                                                    {user.name ??
                                                        "Unnamed User"}
                                                </p>
                                                <p
                                                    className={cn(
                                                        "text-sm text-v3-N-300",
                                                        customization?.descriptionClassName,
                                                    )}
                                                >
                                                    {user.email}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() =>
                                                removeSelectedUser(user.id)
                                            }
                                            className="p-1 text-v3-N-300 hover:text-white"
                                        >
                                            <LuX className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <ModalFooter>
                <Button variant="secondary" onClick={() => onOpenChange(false)}>
                    Cancel
                </Button>
                <Button
                    onClick={handleAddHosts}
                    disabled={isLoading || selectedUsers.length === 0}
                >
                    {isLoading
                        ? "Adding..."
                        : selectedUsers.length === 1
                          ? "Add Host"
                          : `Add ${selectedUsers.length} Hosts`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};
